// Libs
import classNames from 'classnames/bind';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Box, Button, Stack, Tab } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
// Components, Layouts, Pages
import { BaseTabList, BaseToolbar } from '~/components/common';
import {
  CallHistoryOrderTab,
  DocumentOrderTab,
  InfoOrderTab,
  InvoiceOrderTab,
  OrderCostingTab,
  OverbookingOrderTab,
} from '~/components/module/order';
// Others
import { StorageEnum } from '~/utils/enum/base';
import { EMPTY_STRING } from '~/utils/constants/common';
import { IBaseTabItem } from '~/utils/interface/common';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getOrderDetail } from '~/thunks/order/orderThunk';
import { adminRouteAbsolute } from '~/utils/routes/roleRoutes/admin';
import { crewRouteAbsolute } from '~/utils/routes/roleRoutes/crew';
import { loaderRouteAbsolute } from '~/utils/routes/roleRoutes/loader';
import { foremanRouteAbsolute } from '~/utils/routes/roleRoutes/foreman';
import { salesAdminRouteAbsolute } from '~/utils/routes/roleRoutes/salesAdmin';
import { salesPersonRouteAbsolute } from '~/utils/routes/roleRoutes/salesPerson';
import { warehouseLeadRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseLead';
import { warehouseStaffRouteAbsolute } from '~/utils/routes/roleRoutes/warehouseStaff';
import { operationsManagerRouteAbsolute } from '~/utils/routes/roleRoutes/operationsManager';
import { OrderStatusEnum, OrderTabEnum, RoleEnum, TypeQuoteDetailEnum } from '~/utils/enum/common';
import { orderActions, selectIsRefreshOrderDetail } from '~/thunks/order/orderSlice';
import { IOrderDetail, IParamQueryOrderDetail } from '~/utils/interface/order';
import { ORDER_PERMISSIONS } from '~/utils/constants/permissions';
import { usePermissions } from '~/utils/hooks/userPermissions';
// Styles, images, icons
import styles from './OrderDetail.module.scss';
import { icons } from '~/assets';

type Props = {};

const cx = classNames.bind(styles);

const OrderDetail = (props: Props) => {
  //#region Destructuring Props
  const {} = props;

  const { canEditOrders } = usePermissions(ORDER_PERMISSIONS);
  const dispatch = useAppDispatch();
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/order', 'common/base']);
  const { hash, pathname, search } = useLocation();
  const navigate = useNavigate();
  const { id: orderId } = useParams();
  const currentRole: string = JSON.parse(localStorage.getItem(StorageEnum.ROLE)!)[0] ?? EMPTY_STRING;

  //#endregion Declare Hook

  //#region Selector
  const isRefreshOrderDetail = useSelector(selectIsRefreshOrderDetail);
  //#endregion Selector

  //#region Declare State
  const [tabKey, setTabKey] = useState<string>(OrderTabEnum.INFORMATION);
  const [tabItems, setTabItems] = useState<IBaseTabItem[]>([]);
  const [detailOrder, setDetailOrder] = useState<IOrderDetail>();
  //#endregion Declare State

  //#region Declare Memo
  const getMenuItems = useCallback(() => {
    const items: Array<IBaseTabItem> = [{ label: OrderTabEnum.INFORMATION, component: <InfoOrderTab /> }];
    items.push({ label: OrderTabEnum.INVOICE, component: <InvoiceOrderTab /> });

    if (Number(detailOrder?.numberOverbooking || 0) > 0) {
      items.push({
        label: OrderTabEnum.OVERBOOKING,
        component: <OverbookingOrderTab />,
        number: Number(detailOrder?.numberOverbooking || 0),
      });
    }

    items.push({ label: OrderTabEnum.DOCUMENTS, component: <DocumentOrderTab /> });
    items.push({ label: OrderTabEnum.CALL_HISTORY, component: <CallHistoryOrderTab /> });
    items.push({ label: OrderTabEnum.COSTING, component: <OrderCostingTab /> });
    setTabItems(items);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(detailOrder)]);
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    if (hash) setTabKey(hash.slice(1));
  }, [hash]);

  useEffect(() => {
    if (!orderId) return;

    if (!hash) {
      setTabKey(OrderTabEnum.INFORMATION);
    }

    getMenuItems();
  }, [orderId, hash, getMenuItems]);

  useEffect(() => {
    const params: IParamQueryOrderDetail = {
      orderId: orderId || EMPTY_STRING,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    handleGetOrderDetail(params);
  }, []);

  useEffect(() => {
    if (!isRefreshOrderDetail) return;

    const params: IParamQueryOrderDetail = {
      orderId: orderId || EMPTY_STRING,
      type: TypeQuoteDetailEnum.OVERVIEW,
    };

    handleGetOrderDetail(params);

    dispatch(orderActions.setRefreshOrderDetail(false));
  }, [isRefreshOrderDetail]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetOrderDetail = (params: IParamQueryOrderDetail) => {
    dispatch(getOrderDetail(params))
      .unwrap()
      .then((res) => {
        const { data } = res;

        setDetailOrder(data as IOrderDetail);
      })
      .catch((_error) => {})
      .finally(() => {});
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    navigate({
      pathname,
      search,
      hash: `#${newValue}`,
    });
  };

  const renderTabContent = () => {
    const item = tabItems.find((tabItem: IBaseTabItem) => {
      return tabKey === tabItem.label;
    });

    return item?.component;
  };

  const handleOnBack = () => {
    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.order);
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.order);
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.order);
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.order);
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.order);
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.order);
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.order);
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.order);
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.order);
        break;
    }
  };

  const handleEdit = () => {
    if (!orderId) return;

    switch (currentRole) {
      case RoleEnum.SUPER_ADMIN:
        navigate(adminRouteAbsolute.updateOrder(orderId));
        break;
      case RoleEnum.SALES_ADMIN:
        navigate(salesAdminRouteAbsolute.updateOrder(orderId));
        break;
      case RoleEnum.SALES_PERSON:
        navigate(salesPersonRouteAbsolute.updateOrder(orderId));
        break;
      case RoleEnum.OPERATIONS_MANAGER:
        navigate(operationsManagerRouteAbsolute.updateOrder(orderId));
        break;
      case RoleEnum.WAREHOUSE_LEAD:
        navigate(warehouseLeadRouteAbsolute.updateOrder(orderId));
        break;
      case RoleEnum.WAREHOUSE_STAFF:
        navigate(warehouseStaffRouteAbsolute.updateOrder(orderId));
        break;
      case RoleEnum.FOREMAN:
        navigate(foremanRouteAbsolute.updateOrder(orderId));
        break;
      case RoleEnum.CREW:
        navigate(crewRouteAbsolute.updateOrder(orderId));
        break;
      case RoleEnum.LOADER:
        navigate(loaderRouteAbsolute.updateOrder(orderId));
        break;
    }
  };
  //#endregion Handle Function

  return (
    <Box id='orderDetailPage' className={cx('container')}>
      <BaseToolbar title={t('order_detail_title')} isBack onBackToPreviousPage={handleOnBack}>
        {tabKey === OrderTabEnum.INFORMATION && canEditOrders && detailOrder?.status !== OrderStatusEnum.FINALIZED && (
          <Button
            variant='contained'
            onClick={handleEdit}
            startIcon={<img src={icons.commonIconPen} alt={t('common_alt_img')} />}
          >
            {t('table_action_edit', { ns: 'common/base' })}
          </Button>
        )}
      </BaseToolbar>

      <Stack height={5} />

      <BaseTabList value={tabKey} onChange={handleTabChange} type='small'>
        {tabItems.map((item, index) => (
          <Tab
            value={item.label}
            key={index}
            className={cx('tabItem', { active: tabKey === item.label })}
            label={
              <Box className={cx('tabLabel')}>
                <Box>{t(`tab_label_${item.label}`)}</Box>

                {item.number !== undefined && <Box className={cx('tabBadge')}>{item.number}</Box>}
              </Box>
            }
          />
        ))}
      </BaseTabList>

      <Box component={'div'} className={cx('body')}>
        <Box component={'div'} className={cx('tabContent')} key={orderId}>
          {renderTabContent()}
        </Box>
      </Box>
    </Box>
  );
};

export default OrderDetail;
